import { useToastStore } from '../stores/useToastStore';
import { AlertType } from '../types';
import { ApiError } from './ApiError';
import i18n from '../i18n';
import ConfigurationService from '../services/ConfigurationService';
import logger from '../services/loggerService';
import { CircuitBreakerError } from './CircuitBreaker';
import { sanitizeInput } from './sanitizer';
import { RETRYABLE_HTTP_STATUS_CODES, NON_RETRYABLE_HTTP_STATUS_CODES } from '../constants/apiConstants';


/**
 * Map of error codes to user-friendly messages.
 * These messages provide clear feedback without exposing system internals.
 */

/**
 * Map of error codes to user-friendly messages.
 * These messages provide clear feedback without exposing system internals.
 */
/**
 * Centralized error handler for consistent error processing across the application.
 * Handles ApiError instances and generic errors with proper logging and user feedback.
 */
export function handleError(
  error: unknown,
  context?: string,
  defaultMessage: string = i18n.t('common.unexpectedError')
): void {
  if (isCancelledError(error)) {
    logger.debug('Ignoring cancelled/aborted request error', { context, error });
    return;
  }

  const addToast = useToastStore.getState().addToast;

  // Handle ApiError instances (from our API client) OR Duck-Typed Error Objects
  // This ensures we catch errors even if the instance check fails due to serialization/bundling issues

  // Define structure for duck-typing check
  type ApiErrorLike = {
    status?: number;
    data: {
      errorCode: string;
      message: string;
      details?: unknown;
    };
  };

  const isApiErrorLike = (err: unknown): err is ApiErrorLike => {
    return (
      typeof err === 'object' &&
      err !== null &&
      'data' in err &&
      typeof (err as Record<string, unknown>).data === 'object' &&
      (err as Record<string, unknown>).data !== null &&
      typeof ((err as Record<string, unknown>).data as Record<string, unknown>).errorCode === 'string'
    );
  };

  if (error instanceof ApiError || isApiErrorLike(error)) {
    const errorData = error instanceof ApiError ? error.data : error.data;
    const { errorCode, message, details } = errorData;

    // Get user-friendly message from translation or use API message
    // Cast details to Record<string, unknown> for i18n compatibility if it's an object
    const i18nOptions = typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)
      : undefined;

    const isProduction = ConfigurationService.isProduction();

    let userMessage = i18n.exists(`errors.codes.${errorCode}`)
      ? i18n.t(`errors.codes.${errorCode}`, i18nOptions)
      : isProduction && errorCode !== 'VALIDATION_ERROR' && errorCode !== 'VALIDATION_CONFLICT'
        ? defaultMessage
        : sanitizeInput(message) || defaultMessage;

    // Standard 3.1: Actionable Error Categorization
    // Map specific error codes to user-friendly Action Instructions
    const ACTIONABLE_MAP: Record<string, string> = {
      'OFFLINE_QUEUED': i18n.t('errors.actionable.offlineQueued') || 'You are offline. Action queued for sync.',
      'TIMEOUT_ERROR': i18n.t('errors.actionable.timeout') || 'Request timed out. Please try again later.',
      'NETWORK_ERROR': i18n.t('errors.actionable.checkConnection') || 'Connection failed. Please check your internet.',
      'VALIDATION_ERROR': i18n.t('errors.actionable.fixInput') || 'Please fix the highlighted errors.',
      'SESSION_EXPIRED': i18n.t('errors.actionable.loginAgain') || 'Session expired. Please log in again.',
      'FORBIDDEN': i18n.t('errors.actionable.accessDenied') || 'You do not have permission to access this resource.',
      'CONFLICT': sanitizeInput(message) || i18n.t('errors.actionable.conflict') || 'This record was modified by another user. Please refresh and try again.',
    };

    if (errorCode && ACTIONABLE_MAP[errorCode]) {
      userMessage = ACTIONABLE_MAP[errorCode];
    }

    // Show toast notification to user
    addToast({
      type: errorCode === 'OFFLINE_QUEUED' ? AlertType.Success : AlertType.Error,
      message: userMessage,
    });

    // Log for debugging (includes context and full error data)
    // SECURITY: Ensure log data is sanitized to avoid log injection
    const logData = {
      errorCode: sanitizeInput(errorCode),
      message: sanitizeInput(message),
      status: error.status,
      details: typeof details === 'string' ? sanitizeInput(details) : details,
    };

    if (context) {
      logger.warn(`API Error [${sanitizeInput(context)}]`, logData);
    } else {
      logger.warn('API Error', logData);
    }

    return;
  }

  // Handle CircuitBreakerError
  if (error instanceof CircuitBreakerError) {
    const userMessage = i18n.t('errors.actionable.serviceUnavailable') || 'Service is temporarily unavailable due to high failure rate. Please try again later.';

    addToast({
      type: AlertType.Error,
      message: userMessage,
    });

    if (context) {
      logger.warn(`Circuit Breaker Open [${context}]`, { message: error.message });
    } else {
      logger.warn('Circuit Breaker Open', { message: error.message });
    }

    return;
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    addToast({
      type: AlertType.Error,
      message: error.message || defaultMessage,
    });

    if (context) {
      logger.error(`Unhandled Error [${context}]`, error);
    } else {
      logger.error('Unhandled Error', error);
    }

    return;
  }

  // Handle unknown error types
  addToast({
    type: AlertType.Error,
    message: defaultMessage,
  });

  if (context) {
    logger.error(`Unknown Error Type [${context}]`, { error });
  } else {
    logger.error('Unknown Error Type', { error });
  }
}

/**
 * Extracts form validation errors from ApiError for form field highlighting.
 * Returns an object mapping field names to error messages.
 *
 * @param error - The error to extract validation errors from
 * @returns Record of field names to error messages
 */
export function getFormErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) {
    return {};
  }

  const { errorCode, details } = error.data;

  // Only process validation errors
  if (errorCode !== 'VALIDATION_ERROR' && errorCode !== 'VALIDATION_CONFLICT') {
    return {};
  }

  // Extract field-level errors from details array
  if (details && Array.isArray(details)) {
    return details.reduce((acc: Record<string, string>, err: unknown) => {
      const e = err as { field?: string; message?: string };
      if (e.field && e.message) {
        acc[e.field] = e.message;
      }
      return acc;
    }, {});
  }

  return {};
}

/**
 * Checks if an error is a user or system request cancellation (AbortError or superseded).
 *
 * @param error - The error to check
 * @returns true if the error is a cancellation
 */
export function isCancelledError(error: unknown): boolean {
  if (!error) return false;

  const isCancellationText = (text: unknown): boolean => {
    if (typeof text !== 'string') return false;
    const lower = text.toLowerCase();
    return (
      lower.includes('abort') ||
      lower.includes('cancel') ||
      lower.includes('superseded') ||
      lower.includes('unmounted') ||
      lower.includes('disposed') ||
      lower.includes('expired')
    );
  };

  // Handle direct string matching for abort reasons
  if (typeof error === 'string') {
    return isCancellationText(error);
  }

  // Handle DOMException or Error objects
  if (error instanceof DOMException || error instanceof Error) {
    if (error.name === 'AbortError') return true;
    if (isCancellationText(error.name) || isCancellationText(error.message)) return true;
    if ('reason' in error && isCancellationText((error as { reason?: unknown }).reason)) return true;
  }

  // Handle generic objects (e.g., rejected reason structures)
  if (typeof error === 'object') {
    const errObj = error as Record<string, unknown>;
    if (
      isCancellationText(errObj.name) ||
      isCancellationText(errObj.message) ||
      isCancellationText(errObj.error) ||
      isCancellationText(errObj.reason)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if an error is a network error (no response from server).
 * Useful for implementing retry logic.
 *
 * @param error - The error to check
 * @returns true if this is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (isCancelledError(error)) {
    return false;
  }

  if (error instanceof ApiError) {
    // Status 0 typically indicates no response from server
    return error.status === 0;
  }

  if (error instanceof Error) {
    // Check for typical network error messages, excluding aborts/cancellations
    const message = error.message.toLowerCase();
    return (
      (message.includes('network') ||
        message.includes('failed to fetch') ||
        message.includes('connection') ||
        message.includes('timeout')) &&
      !message.includes('abort') &&
      !message.includes('cancel')
    );
  }

  return false;
}

/**
 * Interface representing the detailed breakdown of error retryability.
 */
export interface RetryClassification {
  retryable: boolean;
  reason: string;
  category: 'network' | 'timeout' | 'server' | 'client' | 'cancelled' | 'unknown';
}

/**
 * Classifies an error to determine if it is retryable and provides contextual reason/category.
 *
 * @param error - The error to classify
 * @returns RetryClassification detailing retryability decision
 */
export function getRetryClassification(error: unknown): RetryClassification {
  if (isCancelledError(error)) {
    return {
      retryable: false,
      reason: 'Request cancelled',
      category: 'cancelled',
    };
  }

  if (error instanceof ApiError) {
    const status = error.status;

    if ((RETRYABLE_HTTP_STATUS_CODES as readonly number[]).includes(status)) {
      return {
        retryable: true,
        reason: `Retryable HTTP status: ${status}`,
        category: status === 504 ? 'timeout' : 'server',
      };
    }

    if ((NON_RETRYABLE_HTTP_STATUS_CODES as readonly number[]).includes(status)) {
      return {
        retryable: false,
        reason: `Non-retryable HTTP status: ${status}`,
        category: 'client',
      };
    }

    if (status >= 500) {
      return {
        retryable: true,
        reason: `Server error status: ${status}`,
        category: 'server',
      };
    }

    if (status === 0) {
      return {
        retryable: true,
        reason: 'Network connectivity failure (status 0)',
        category: 'network',
      };
    }

    return {
      retryable: false,
      reason: `Unhandled HTTP status: ${status}`,
      category: status >= 400 && status < 500 ? 'client' : 'unknown',
    };
  }

  if (isNetworkError(error)) {
    const message = error instanceof Error ? error.message : String(error);
    const category = message.toLowerCase().includes('timeout') ? 'timeout' : 'network';
    return {
      retryable: true,
      reason: `Network/Timeout error: ${message}`,
      category,
    };
  }

  if (error instanceof Error) {
    return {
      retryable: false,
      reason: `Generic unretryable error: ${error.message}`,
      category: 'unknown',
    };
  }

  return {
    retryable: false,
    reason: 'Unknown error format',
    category: 'unknown',
  };
}

/**
 * Checks if an error is retryable (5xx server errors or network errors).
 * Use this to determine if an operation should be retried.
 *
 * @param error - The error to check
 * @returns true if the error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  return getRetryClassification(error).retryable;
}

